import {
  ComponentHeader,
  ComponentHeaderProps,
} from "design-system/components/primitives/component-header/component-header";
import { CtaLinkProps } from "design-system/components/primitives/cta-link/cta-link";
import { CtaList } from "design-system/components/primitives/cta-list/cta-list";
import { Link } from "design-system/components/primitives/link/link";
import { Alignment, EditAttributes } from "design-system/types/types";
import "./alphanumeric-link-list.scss";

export interface AlphanumericLinkListProps {
  /** Optional section heading */
  headerContent?: ComponentHeaderProps;
  items: Array<CtaLinkProps>;
  HeadingLevel?: "h2" | "h3" | "h4";
  align?: Alignment;
  editAttributes?: {
    block?: EditAttributes;
  };
}

export function AlphanumericLinkList({
  items,
  headerContent,
  align = "full",
  HeadingLevel = "h2",
  editAttributes,
}: AlphanumericLinkListProps) {
  const sortedItems = items.sort((a, b) => {
    if (
      a.children &&
      typeof a.children === "string" &&
      b.children &&
      typeof b.children === "string"
    ) {
      return a.children.localeCompare(b.children);
    } else {
      return 0;
    }
  });

  const alphabeticalList = sortedItems.reduce(
    (acc, item) => {
      if (item.children && typeof item.children === "string") {
        const letter = item.children.toString().charAt(0).toUpperCase();
        const letterObj = acc.find((obj) => obj.letter === letter);

        if (!letterObj) {
          acc.push({
            letter: letter,
            items: [item],
          });
        } else {
          letterObj.items.push(item);
        }
      }

      return acc;
    },
    [] as Array<{ letter: string; items: Array<CtaLinkProps> }>,
  );

  return (
    <div
      className={`hbs-component--alphanumeric-link-list hbs-global-align-${align}`}
    >
      {headerContent && <ComponentHeader {...headerContent} />}
      <div className="hbs-alphanumeric-link-list" {...editAttributes?.block}>
        <Link
          href="#skipto-end"
          className="hbs-global-visually-hidden skiptoend noindex"
          target="_self"
        >
          Skip A to Z Links
        </Link>

        {alphabeticalList?.map(({ letter, items }, i) => (
          <div key={i} className="hbs-alphanumeric-link-list__wrapper">
            <HeadingLevel className="hbs-alphanumeric-link-list__letter">
              {letter}
            </HeadingLevel>
            {items && items.length > 0 && (
              <div className="hbs-alphanumeric-link-list__items">
                <CtaList
                  items={items}
                  className={`hbs-alphanumeric-link-list__list hbs-alphanumeric-link-list__list__${align}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div id="skipto-end" className="hbs-global-visually-hidden" tabIndex={-1}>
        End of A to Z List
      </div>
    </div>
  );
}
